import React, { useEffect, useRef } from 'react';
import smoothScrollIntoView from 'smooth-scroll-into-view-if-needed';
import {
  allowsFutureStartDate,
  getVariantSetupFeeName,
  hasSetupFeeVariant,
  isFreeVariant,
} from '@wix/pricing-plans-utils';
import { Text, Divider } from '@wix/wix-ui-tpa/cssVars';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { getOrderCoupon, getPricesCoupon, hasOrderCoupon } from '../../../utils/checkout';
import { CouponInput } from '../CouponInput';
import { DateInput } from './DateInput';
import { classes, st } from './Small.st.css';
import { SummaryProps } from './types';
import { useOrderSummary } from './useOrderSummary';
import { usePresenter } from './usePresenter';

export const SmallSummary: React.FC<SummaryProps> = ({
  plan,
  prices = [],
  benefits,
  order,
  startDate,
  setStartDate,
  minStartDate,
  maxStartDate,
  isStartDateDisabled,
  updatePriceDetails,
  updatePriceDetailsError,
  couponInputMode,
  couponLoading,
  couponCode,
  removeCoupon,
  applyCouponError,
  hasCoupons,
}) => {
  const { t } = useTranslation();
  const summaryRef = useRef<HTMLDivElement>(null);
  const variant = plan.pricingVariants?.[0] ?? {};
  const presenter = usePresenter({ plan, prices, benefits, order });
  const orderSummary = useOrderSummary(plan, prices, order);
  const { total, subtotal, taxAmount, couponAmount, planPrice, setupFee } = orderSummary;

  const isCouponsEnabled = hasCoupons && !isFreeVariant(variant);
  const hasCouponOnOrder = order && hasOrderCoupon(order);
  const couponId = hasCouponOnOrder ? getOrderCoupon(order!)?.id : getPricesCoupon(prices)?.id;

  const paymentDisclaimer = presenter.getPaymentDisclaimer();
  const nextPayment = presenter.getNextPayment();

  useEffect(() => {
    if (!summaryRef.current) {
      return;
    }

    const couponWasApplied = !couponLoading && couponInputMode === 'coupon' && couponCode;
    if (couponWasApplied) {
      smoothScrollIntoView(summaryRef.current, { scrollMode: 'if-needed' });
    }
  }, [couponCode, couponInputMode, couponLoading]);

  return (
    <div className={classes.box} ref={summaryRef}>
      <div className={classes.section}>
        <Text className={classes.heading} tagName="h2">
          {t('payment.checkout-summary.heading')}
        </Text>
        {allowsFutureStartDate(variant) ? (
          <DateInput
            label={t('payment.mini-checkout-summary.start-date')}
            disabled={isStartDateDisabled}
            dataHook="small-start-date-input"
            value={startDate}
            max={maxStartDate}
            min={minStartDate}
            onChange={setStartDate}
          />
        ) : null}
      </div>
      <div className={classes.section}>
        <div className={st(classes.entry, { noWrap: true })}>
          <Text className={classes.labelBold} data-hook="payment-checkout-summary-plan-name">
            {presenter.getPlanName()}
          </Text>
          <Text className={classes.text}>{planPrice}</Text>
        </div>
        <Text className={classes.textLight} data-hook="payment-checkout-small-summary-duration">
          {presenter.getDurationFormatted()}
        </Text>
        {presenter.showFreeTrial() && (
          <Text className={classes.textLight} data-hook="payment-checkout-small-summary-plan-free-trial">
            {presenter.getFreeTrialFormatted()}
          </Text>
        )}
        {presenter.hasSessions() && (
          <Text className={classes.textLight} data-hook="payment-checkout-small-summary-sessions">
            {presenter.getSessionFormatted()}
          </Text>
        )}
      </div>
      {hasSetupFeeVariant(variant) && (
        <div className={classes.section}>
          <div className={st(classes.entry, { noWrap: true })}>
            <Text className={classes.labelBold}>{getVariantSetupFeeName(variant)}</Text>
            <Text className={classes.text}>{setupFee}</Text>
          </div>
        </div>
      )}
      {isCouponsEnabled ? (
        <>
          <Divider className={classes.divider} />
          <CouponInput
            disabled={hasCouponOnOrder || isStartDateDisabled}
            mode={hasCouponOnOrder ? 'coupon' : couponInputMode}
            error={updatePriceDetailsError ?? applyCouponError}
            value={hasCouponOnOrder ? getOrderCoupon(order!)!.code! : couponCode}
            onApplyClick={(code) => updatePriceDetails(plan.id!, code)}
            onRemoveClick={() => removeCoupon(plan.id!, couponId!)}
            loading={couponLoading}
          />
        </>
      ) : null}
      {((taxAmount || presenter.hasCoupon()) && subtotal) ||
      (presenter.hasCoupon() && subtotal) ||
      (taxAmount && subtotal) ? (
        <>
          <Divider className={classes.divider} />
          <div className={classes.section}>
            {(taxAmount || presenter.hasCoupon()) && subtotal ? (
              <div className={classes.entry} data-hook="payment-checkout-small-summary-subtotal-row">
                <Text className={classes.text} data-hook="payment-checkout-small-summary-subtotal-label">
                  {t('payment.checkout-summary.subtotal')}
                </Text>
                <Text className={classes.text} data-hook="payment-checkout-small-summary-subtotal">
                  {subtotal}
                </Text>
              </div>
            ) : null}
            {presenter.hasCoupon() && subtotal ? (
              <div className={classes.entry} data-hook="payment-checkout-small-summary-coupon-discount-row">
                <Text className={classes.text} data-hook="payment-checkout-small-summary-coupon-discount-label">
                  {t('payment.checkout-summary.coupon-code')}
                </Text>
                <Text className={classes.text} data-hook="payment-checkout-small-summary-coupon-discount">
                  {couponAmount}
                </Text>
              </div>
            ) : null}
            {taxAmount && subtotal ? (
              <div className={classes.entry} data-hook="payment-checkout-small-summary-tax-row">
                <Text className={classes.text} data-hook="payment-checkout-small-summary-tax-label">
                  {presenter.getTaxName()} ({presenter.getTaxRate()} %)
                </Text>
                <Text className={classes.text} data-hook="payment-checkout-small-summary-tax">
                  {taxAmount}
                </Text>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
      <Divider className={classes.divider} />
      <div className={classes.section}>
        <div className={classes.entry}>
          <Text className={classes.labelLarge} data-hook="payment-checkout-small-summary-plan-total">
            {presenter.getTotalLabel()}
          </Text>
          <Text className={classes.valueLarge} data-hook="payment-checkout-small-summary-price">
            {total}
          </Text>
        </div>
        {nextPayment && (
          <div className={classes.entry}>
            <Text className={classes.text}>{nextPayment.label}</Text>
            <Text className={classes.text}>{nextPayment.amount}</Text>
          </div>
        )}
        {presenter.showPaymentPeriod() ? (
          <div className={classes.paymentPeriodEntry}>
            <Text className={classes.paymentPeriod}>{presenter.getPaymentPeriod()}</Text>
          </div>
        ) : null}
      </div>
      {paymentDisclaimer ? (
        <div className={classes.section}>
          <div className={classes.entry}>
            <Text className={classes.text}>{paymentDisclaimer}</Text>
          </div>
        </div>
      ) : null}
    </div>
  );
};
